import { useInfiniteQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';

export type HistoryEntry = {
  id: string;
  type: string;
  label: string;
  resource: string | null;
  entityId: string;
  occurredAt: string;
  actor: { id: string; name: string } | null;
  /** I3: the system wrote it on behalf of a person (e.g. the rules' quote lines after a change of an event's scope). */
  causedBy?: { kind: string; user: { id: string; name: string } | null; eventId: string | null } | null;
  changes: Record<string, unknown>;
  fieldLabels: Record<string, string>;
};
type Page = { entries: HistoryEntry[]; nextBefore: string | null };

/** Labels of payload keys that the server's resource catalogue does not describe (generic event details). */
const DETAIL_LABELS: Record<string, string> = {
  name: 'Imię i nazwisko',
  email: 'E-mail',
  active: 'Konto aktywne',
  roleIds: 'Role',
  change: 'Zmiana',
  by: 'Kto ustawił',
  target: 'Dotyczy',
  permissions: 'Liczba uprawnień',
  fields: 'Liczba reguł pól',
  overrides: 'Liczba wyjątków',
  label: 'Nazwa',
  mergedWith: 'Połączono z',
};
/** Keys that are only technical ids; the label of the event already says what happened. */
const HIDDEN_KEYS = new Set(['changed', 'userId', 'roleId', 'copyFrom', 'eventId']);
/** What made the system write (I3). */
const CAUSES: Record<string, string> = { scope: 'zmiana zakresu prac', quote: 'nowy kosztorys' };
const VALUE_LABELS: Record<string, Record<string, string>> = {
  by: { self: 'sam użytkownik', owner: 'właściciel' },
  target: { user: 'konto', role: 'rola' },
  change: { updated: 'zmieniono treść', reverted: 'przywrócono treść domyślną' },
};

const WHEN = new Intl.DateTimeFormat('pl-PL', {
  timeZone: 'Europe/Warsaw',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** "18 października 2026 14:05" in Warsaw time, whatever the browser's zone. */
export const formatWhen = (iso: string): string => WHEN.format(new Date(iso));

export type Detail = { key: string; label: string; value: string };

function formatValue(key: string, value: unknown): string | null {
  if (typeof value === 'boolean') return value ? 'tak' : 'nie';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return VALUE_LABELS[key]?.[value] ?? value;
  if (Array.isArray(value)) return value.length === 0 ? 'brak' : String(value.length);
  return null;
}

/** Human-readable lines for an entry: changed field names first, then the other details. Raw keys never show. */
export function describeChanges(entry: Pick<HistoryEntry, 'changes' | 'fieldLabels'>): Detail[] {
  const labelOf = (key: string) => entry.fieldLabels[key] ?? DETAIL_LABELS[key] ?? null;
  const out: Detail[] = [];
  const changed = entry.changes.changed;
  if (Array.isArray(changed) && changed.length > 0) {
    const names = changed.map((n) => labelOf(String(n))).filter((n): n is string => n !== null);
    if (names.length > 0) out.push({ key: 'changed', label: 'Zmienione pola', value: names.join(', ') });
  }
  for (const [key, raw] of Object.entries(entry.changes)) {
    if (HIDDEN_KEYS.has(key)) continue;
    const label = labelOf(key);
    const value = formatValue(key, raw);
    if (label !== null && value !== null) out.push({ key, label, value });
  }
  return out;
}

/** Timeline of changes for one record, newest first. Everything shown was already filtered by the server. */
export function HistoryPanel({ entityId, resource }: { entityId: string; resource: string }) {
  const query = useInfiniteQuery({
    queryKey: ['history', resource, entityId],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ entityId, resource, limit: '20' });
      if (pageParam) params.set('before', pageParam);
      return api<Page>(`/api/history?${params.toString()}`);
    },
    getNextPageParam: (last) => last.nextBefore,
  });
  const entries = query.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <section className="history" aria-label="Historia zmian" data-feature-id="history.view">
      <h3>Historia zmian</h3>
      {query.isLoading && <p className="muted" role="status">Wczytywanie historii…</p>}
      {query.error && (
        <p className="alert" role="alert">
          {query.error instanceof ApiError ? query.error.message : 'Nie udało się wczytać historii'}
        </p>
      )}
      {query.data && entries.length === 0 && <p className="muted">Nie zapisano jeszcze żadnych zmian.</p>}
      {entries.length > 0 && (
        <ol className="history-list">
          {entries.map((entry) => {
            const details = describeChanges(entry);
            return (
              <li key={entry.id} className="history-item">
                <div className="history-head">
                  <strong>{entry.label}</strong>
                  <time dateTime={entry.occurredAt}>{formatWhen(entry.occurredAt)}</time>
                </div>
                <div className="muted">
                  {entry.actor ? entry.actor.name : entry.causedBy ? `System — ${CAUSES[entry.causedBy.kind] ?? 'zmiana'}${entry.causedBy.user ? `: ${entry.causedBy.user.name}` : ''}` : 'System'}
                  {!entry.actor && entry.causedBy?.eventId && (
                    <>
                      {' '}
                      (<a href={`/wydarzenia?wydarzenie=${encodeURIComponent(entry.causedBy.eventId)}`}>wydarzenie</a>)
                    </>
                  )}
                </div>
                {details.length > 0 && (
                  <dl className="history-details">
                    {details.map((d) => (
                      <div key={d.key}>
                        <dt>{d.label}</dt>
                        <dd>{d.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </li>
            );
          })}
        </ol>
      )}
      {query.hasNextPage && (
        <button className="btn small" type="button" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
          {query.isFetchingNextPage ? 'Wczytywanie…' : 'Pokaż starsze'}
        </button>
      )}
    </section>
  );
}
